import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AccountInfo, { getInitials, getRoleLabel, getUserEmail, getUserRole } from './AccountInfo';
import AiAssistantButton from './AiAssistantButton';
import HeaderFleetStrip from './HeaderFleetStrip';
import LanguageSwitcher from './LanguageSwitcher';

const Header = ({ isSidebarVisible, toggleSidebar, onLogout, userEmail: propUserEmail, userRole: propUserRole }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Получаем данные из пропсов или localStorage
  const userEmail = getUserEmail(propUserEmail);
  const userRole = getUserRole(propUserRole);
  const initials = getInitials(userEmail);
  const roleLabel = getRoleLabel(userRole, t);

  // Обработчик для toggle sidebar с защитой
  const handleToggleSidebar = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof toggleSidebar === 'function') {
      toggleSidebar();
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 flex h-[81px] items-center bg-surface-1 px-3 text-muted-100 sm:px-4 lg:px-8">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex shrink-0 items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={handleToggleSidebar}
            aria-pressed={isSidebarVisible}
            className="relative z-50 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl bg-white/[0.04] text-muted-300 transition hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <span className="material-icons pointer-events-none text-[22px]">
              {isSidebarVisible ? 'menu_open' : 'menu'}
            </span>
          </button>
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-3 sm:gap-4">
          <HeaderFleetStrip />
          <div className="hidden h-9 items-center gap-2 rounded-xl bg-white/[0.04] px-3 text-sm text-muted-300 lg:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-positive" aria-hidden />
            <span>{t('header.systemStable')}</span>
          </div>

          <AiAssistantButton />

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((prev) => !prev)}
              className={`group flex items-center gap-3 rounded-xl bg-white/[0.04] px-2 py-1.5 transition hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${dropdownOpen ? 'bg-white/[0.08] text-white' : 'text-muted-200'}`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-400/15 font-display text-xs text-brand-100">
                {initials}
              </span>
              <span className="hidden min-w-[120px] flex-col items-start sm:flex">
                <span className="text-sm font-semibold text-white">
                  {userEmail || t('common.account')}
                </span>
                {roleLabel && (
                  <span className="text-xs text-muted-400">{roleLabel}</span>
                )}
              </span>
              <span
                className={`material-icons text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-brand-300' : 'text-muted-400'}`}
              >
                expand_more
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-2xl bg-surface-2 text-muted-100 shadow-elevated">
                <div className="px-4 py-4">
                  <AccountInfo userEmail={userEmail} userRole={userRole} size="lg" />
                </div>
                <div className="flex flex-col gap-1 px-2 pb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false);
                      navigate('/profile');
                    }}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-300 transition hover:bg-white/[0.06] hover:text-white"
                  >
                    <span className="material-icons text-[20px] text-muted-400">account_circle</span>
                    {t('common.myProfile')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false);
                      if (typeof onLogout === 'function') {
                        onLogout();
                      }
                    }}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-300 transition hover:bg-critical/10 hover:text-critical"
                  >
                    <span className="material-icons text-[20px]">logout</span>
                    {t('common.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
